/**
 * Travel Map Component
 * 
 * Displays visited countries and cities (simplified version)
 * Full map visualization can be enhanced later with Mapbox integration
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  View,
  StyleSheet,
  ScrollView,
  RefreshControl,
  ActivityIndicator,
  Pressable,
} from 'react-native';
import { useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { Screen } from '@/components/layout/Screen';
import { Heading, Body, Caption } from '@/components/ui/Text';
import { Card } from '@/components/ui/Card';
import { useTheme } from '@/theme';
import { useUserStore } from '@/state/userStore';
import { getUserStats, updateUserStatsFromTrips, type UserStats } from '@/services/userStats';
import { fetchUserTrips } from '@/services/trips';
import { VisitedCountriesMap } from './VisitedCountriesMap';

export function TravelMap() {
  const theme = useTheme();
  const router = useRouter();
  const handleClose = () => {
    if (typeof router.canGoBack === 'function' && router.canGoBack()) {
      router.back();
      return;
    }
    router.replace('/(tabs)/profile');
  };

  const firebaseUser = useUserStore((state) => state.firebaseUser);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [statsLoadIssue, setStatsLoadIssue] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedYear, setSelectedYear] = useState<number | 'all'>(new Date().getFullYear());

  useEffect(() => {
    loadStats();
  }, [firebaseUser?.uid]);

  const loadStats = async () => {
    if (!firebaseUser?.uid) return;

    try {
      setLoading(true);
      setStatsLoadIssue(null);
      const { trips } = await fetchUserTrips(firebaseUser.uid);
      let userStats: UserStats | null = null;
      if (trips.length > 0) {
        let rebuildFailed = false;
        try {
          userStats = await updateUserStatsFromTrips(firebaseUser.uid, trips);
        } catch (error) {
          rebuildFailed = true;
          console.error('[TravelMap] Failed to rebuild user stats from trips:', error);
          userStats = await getUserStats(firebaseUser.uid);
        }
        if (rebuildFailed) {
          setStatsLoadIssue(
            userStats
              ? 'Travel stats could not be rebuilt from trips. Showing the last saved stats instead.'
              : 'Travel stats could not be rebuilt from trips, so no stats are currently available.'
          );
        }
      } else {
        userStats = await getUserStats(firebaseUser.uid);
      }
      setStats(userStats);
    } catch (error) {
      console.error('[TravelMap] Error loading stats:', error);
      setStatsLoadIssue('Travel stats could not be loaded right now. Pull to refresh and try again.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const onRefresh = async () => {
    setRefreshing(true);
    await loadStats();
  };

  // Get available years from stats
  const getAvailableYears = (): number[] => {
    if (!stats) return [];
    const years = new Set<number>();
    stats.countriesVisited.forEach(country => {
      country.years.forEach(year => years.add(year));
    });
    return Array.from(years).sort((a, b) => b - a);
  };

  const filteredCountries = useMemo(() => {
    if (!stats) return [];
    if (selectedYear === 'all') return stats.countriesVisited;
    return stats.countriesVisited.filter((country) =>
      country.years.includes(selectedYear as number)
    );
  }, [stats, selectedYear]);

  const filteredCities = useMemo(() => {
    if (!stats) return [];
    if (selectedYear === 'all') return stats.citiesVisited;
    const y = selectedYear as number;
    return stats.citiesVisited.filter((c) => c.firstVisit.getFullYear() === y);
  }, [stats, selectedYear]);

  const citiesForPins = useMemo(
    () =>
      filteredCities.slice(0, 18).map((c) => ({
        key: `${c.city}_${c.country}_${c.firstVisit.getTime()}`,
        city: c.city,
        country: c.country,
      })),
    [filteredCities]
  );

  const chipUnselected = {
    backgroundColor: theme.colors.surface,
    borderWidth: 1,
    borderColor: theme.colors.borderMuted,
  } as const;

  if (loading && !stats) {
    return (
      <Screen>
        <View style={styles.loadingContainer}>
          <ActivityIndicator size="large" color={theme.colors.accent} />
        </View>
      </Screen>
    );
  }

  const availableYears = getAvailableYears();

  return (
    <Screen
      title="Travel Map"
      subtitle="Countries and cities you've visited"
      headerAction={
        <Pressable 
          onPress={handleClose}
          style={({ pressed }) => ({ opacity: pressed ? 0.7 : 1 })}
        >
          <Ionicons name="close" size={24} color={theme.colors.text} />
        </Pressable>
      }
    >
      <ScrollView
        style={styles.container}
        contentContainerStyle={styles.content}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
        }
      >
        {statsLoadIssue ? (
          <Card variant="elevated" elevation={1} padding={16} style={styles.errorCard}>
            <Body style={{ color: theme.colors.textSecondary }}>{statsLoadIssue}</Body>
          </Card>
        ) : null}

        {/* Year Filter */}
        {availableYears.length > 0 && (
          <ScrollView
            horizontal
            showsHorizontalScrollIndicator={false}
            style={styles.yearFilter}
            contentContainerStyle={styles.yearFilterContent}
          >
            <Pressable
              style={[
                styles.yearButton,
                selectedYear === 'all'
                  ? { backgroundColor: theme.colors.accent, borderWidth: 0 }
                  : chipUnselected,
              ]}
              onPress={() => setSelectedYear('all')}
            >
              <Body
                style={[
                  styles.yearButtonText,
                  {
                    color:
                      selectedYear === 'all'
                        ? theme.colors.onAccent
                        : theme.colors.textSecondary,
                  },
                ]}
              >
                All
              </Body>
            </Pressable>
            {availableYears.map((year) => (
              <Pressable
                key={year}
                style={[
                  styles.yearButton,
                  selectedYear === year
                    ? { backgroundColor: theme.colors.accent, borderWidth: 0 }
                    : chipUnselected,
                ]}
                onPress={() => setSelectedYear(year)}
              >
                <Body
                  style={[
                    styles.yearButtonText,
                    {
                      color:
                        selectedYear === year
                          ? theme.colors.onAccent
                          : theme.colors.textSecondary,
                    },
                  ]}
                >
                  {year}
                </Body>
              </Pressable>
            ))}
          </ScrollView>
        )}

        <VisitedCountriesMap
          visitedCountryNames={filteredCountries.map((c) => c.country)}
          citiesForPins={citiesForPins}
        />

        {/* Countries List */}
        {filteredCountries.length > 0 ? (
          <Card variant="elevated" elevation={1} padding={16} style={styles.countriesCard}>
            <Heading style={[styles.sectionTitle, { color: theme.colors.text }]}>
              {selectedYear === 'all' ? 'All Countries' : `${selectedYear} Countries`}
            </Heading>
            <View style={styles.countriesList}>
              {filteredCountries.map((country) => (
                <View
                  key={country.country}
                  style={[
                    styles.countryItem,
                    { borderBottomColor: theme.colors.borderMuted },
                  ]}
                >
                  <View style={styles.countryFlag}>
                    <Body style={[styles.countryName, { color: theme.colors.text }]}>
                      {country.country}
                    </Body>
                  </View>
                  <View style={styles.countryDetails}>
                    <Caption style={{ color: theme.colors.textTertiary }}>
                      {country.visitCount} {country.visitCount === 1 ? 'visit' : 'visits'}
                    </Caption>
                    <Caption style={{ color: theme.colors.textTertiary, fontSize: 10 }}>
                      First: {country.firstVisit.toLocaleDateString()}
                    </Caption>
                  </View>
                </View>
              ))}
            </View>
          </Card>
        ) : (
          <Card variant="elevated" elevation={1} padding={24} style={styles.emptyState}>
            <Body style={{ color: theme.colors.textSecondary, textAlign: 'center' }}>
              {selectedYear === 'all'
                ? 'No countries visited yet. Start planning your first trip!'
                : `No countries visited in ${selectedYear}.`}
            </Body>
          </Card>
        )}

        {/* Cities List */}
        {stats && filteredCities.length > 0 && (
          <Card variant="elevated" elevation={1} padding={16} style={styles.citiesCard}>
            <Heading style={[styles.sectionTitle, { color: theme.colors.text }]}>
              Cities visited
            </Heading>
            <View style={styles.citiesList}>
              {filteredCities.slice(0, 20).map((city) => (
                <View key={`${city.city}_${city.country}_${city.firstVisit.getTime()}`} style={styles.cityItem}>
                  <Body style={[styles.cityName, { color: theme.colors.text }]}>{city.city}</Body>
                  <Caption style={{ color: theme.colors.textTertiary }}>
                    {city.country}
                  </Caption>
                </View>
              ))}
              {filteredCities.length > 20 && (
                <Body style={{ color: theme.colors.textSecondary, marginTop: 8 }}>
                  +{filteredCities.length - 20} more cities
                </Body>
              )}
            </View>
          </Card>
        )}

        {/* Continents */}
        {stats && stats.continentsVisited.length > 0 && (
          <Card variant="elevated" elevation={1} padding={16} style={styles.continentsCard}>
            <Heading style={[styles.sectionTitle, { color: theme.colors.text }]}>Continents</Heading>
            <View style={styles.continentsList}>
              {stats.continentsVisited.map((continent) => (
                <View key={continent} style={[styles.continentItem, chipUnselected]}>
                  <Body style={[styles.continentName, { color: theme.colors.textSecondary }]}>
                    {continent}
                  </Body>
                </View>
              ))}
            </View>
          </Card>
        )}
      </ScrollView>
    </Screen>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  content: {
    padding: 16,
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  yearFilter: {
    marginBottom: 16,
  },
  yearFilterContent: {
    gap: 8,
    paddingRight: 16,
  },
  yearButton: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
  },
  yearButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  countriesCard: {
    marginBottom: 16,
  },
  errorCard: {
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: '700',
    marginBottom: 16,
  },
  countriesList: {
    gap: 12,
  },
  countryItem: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: 12,
    borderBottomWidth: 1,
  },
  countryFlag: {
    flex: 1,
  },
  countryName: {
    fontSize: 16,
    fontWeight: '500',
  },
  countryDetails: {
    alignItems: 'flex-end',
  },
  citiesCard: {
    marginBottom: 16,
  },
  citiesList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
  },
  cityItem: {
    minWidth: '30%',
    marginBottom: 8,
  },
  cityName: {
    fontSize: 14,
    fontWeight: '500',
    marginBottom: 2,
  },
  continentsCard: {
    marginBottom: 16,
  },
  continentsList: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  continentItem: {
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
  },
  continentName: {
    fontSize: 14,
    fontWeight: '500',
  },
  emptyState: {
    marginTop: 24,
  },
});

